import { sanitizeHtml } from './sanitizeHtml';

/**
 * Заголовок задания (`content.title`) — с форматированием, как у текста: курсив,
 * подчёркивание, цвет, заливка (D39, решение владельца 13.09.2026; жирного нет —
 * заголовок и так 700).
 *
 * ⚠️ В БАЗЕ ЛЕЖАТ ДВА ВИДА ЗАГОЛОВКОВ, И ОБА ОСТАЮТСЯ ВЕРНЫМИ.
 *   - Старые — простой текст, годами печатались как есть. В них бывают `&` и `<`
 *     («Fridge & freezer», «a < b»), и показать их надо ровно так же.
 *   - Новые с форматированием — HTML из редактора.
 * Миграции нет. Отличаем по наличию тегов, как `textToHtml` у текстового блока.
 *
 * ⚠️ ЗАГОЛОВОК БЕЗ ФОРМАТИРОВАНИЯ ХРАНИТСЯ ПРОСТЫМ ТЕКСТОМ, а не HTML
 * (`titleFromEditor`). Редактор отдаёт `&amp;` вместо `&`; сохрани мы это как есть —
 * тегов нет, строка сочлась бы простым текстом, экранировалась бы ещё раз, и
 * ученик увидел бы «Fridge &amp; freezer».
 *
 * ⚠️ Заголовок — СТРОКА, а не абзац: `<p>`, `<div>` и `<br>` превращаются в
 * пробел. Он стоит в `<h3>` рядом с номером задания, абзацу там не место.
 */

/** Есть ли в строке разметка — любая, включая блочную. */
const MARKUP = /<(b|i|u|s|strong|em|span|mark|font|br|div|p)[\s>/]/i;
/** Именно форматирование — то, ради чего заголовок хранится как HTML. */
const FORMATTING = /<(b|i|u|s|strong|em|span|mark|font)[\s>/]/i;
const BLOCK_TAGS = /<\/?(p|div)(\s[^>]*)?>|<br\s*\/?>/gi;

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Безопасный HTML заголовка в одну строку — для `dangerouslySetInnerHTML`. */
export function titleHtml(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  if (!MARKUP.test(s)) return escapeHtml(s);
  const inline = s.replace(BLOCK_TAGS, ' ').replace(/\s{2,}/g, ' ').trim();
  return sanitizeHtml(inline).trim();
}

/**
 * Текст заголовка без тегов — для `aria-label`, `title` у рамки, подписи ссылки и
 * проверки «есть ли заголовок». Пустой `<br>` из редактора заголовком не считается.
 *
 * Разбор через `DOMParser`: он не исполняет ничего и не грузит картинки, в отличие
 * от `innerHTML` у созданного элемента.
 */
export function titleText(raw: unknown): string {
  const html = titleHtml(raw);
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Что сохранить из однострочного редактора заголовка.
 *
 * `html` и `text` — `innerHTML` и `textContent` поля. Без форматирования уходит
 * текст (см. шапку), с форматированием — очищенный HTML в одну строку.
 */
export function titleFromEditor(html: string, text: string): string {
  if (text.replace(/\s+/g, '') === '') return '';
  if (!FORMATTING.test(html)) return text.replace(/\n/g, ' ');
  return sanitizeHtml(html.replace(BLOCK_TAGS, ' '));
}
